import { ProgressBar } from "../../../components/ProgressBar";
import {
  DateFormat,
  formatCurrency,
  formatDate,
  goalYearOf,
} from "../../../utils";
import { SavingsGoalMode, SavingsGoalResponse } from "../../../services";
import { OUTCOME_LABEL } from "./constants";
import { savingsGoalStatusOf } from "./goalStatus";
import { SavingsGoalCardProps } from "./types";
import {
  Actions,
  AmountRow,
  Card,
  CardTop,
  CurrentAmount,
  GoalName,
  LinkActions,
  MetaRow,
  NameRow,
  StatusChip,
  TargetAmount,
} from "./styles";
import Button, { ButtonTypes } from "../../../components/Button";

const requiredLineOf = (
  requiredPerMonth: number | null,
  isReached: boolean,
) => {
  if (isReached) {
    return "Nothing more needed";
  }
  if (requiredPerMonth === null) {
    return "Target date passed";
  }
  return formatCurrency(requiredPerMonth) + " / month needed";
};

const followsLineOf = (predecessor: SavingsGoalResponse) => {
  const parts = [
    "Follows " + goalYearOf(predecessor),
    formatCurrency(predecessor.targetAmount),
  ];
  if (predecessor.outcome) {
    parts.push(OUTCOME_LABEL[predecessor.outcome]);
  }
  return parts.join(" · ");
};

const MODE_LABEL: Record<SavingsGoalMode, string> = {
  BALANCE: "Total balance",
  FLOW: "Saved this year",
};

export const SavingsGoalCard = ({
  goal,
  predecessor,
  onEdit,
  onDelete,
}: SavingsGoalCardProps) => {
  const { progress } = goal;
  const targetDate = formatDate(goal.targetDate, DateFormat.DMY_SPACE);
  const modeLabel = MODE_LABEL[goal.mode];
  const isReached = progress.lifecycleStatus === "COMPLETED";
  const status = savingsGoalStatusOf(progress);
  const requiredLine = requiredLineOf(progress.requiredPerMonth, isReached);

  const followsLine = predecessor ? followsLineOf(predecessor) : null;

  return (
    <Card data-testid="savings-goal-card">
      <CardTop>
        <NameRow>
          <GoalName>{goal.name}</GoalName>
        </NameRow>
        <StatusChip $tone={status.tone}>{status.label}</StatusChip>
      </CardTop>

      <AmountRow>
        <CurrentAmount>{formatCurrency(progress.currentAmount)}</CurrentAmount>
        <TargetAmount>of {formatCurrency(progress.targetAmount)}</TargetAmount>
      </AmountRow>

      <ProgressBar
        value={progress.progressPercent}
        max={100}
        color={status.barColor}
        minFillPct={2}
        ariaLabel={goal.name + " progress"}
      />

      <MetaRow>
        <span>
          {modeLabel} · by {targetDate}
        </span>
        <span>{requiredLine}</span>
      </MetaRow>

      {followsLine && (
        <MetaRow data-testid="follows-line">{followsLine}</MetaRow>
      )}

      <Actions>
        <LinkActions>
          <Button
            type={ButtonTypes.link}
            size="small"
            tone="muted"
            onClick={() => onEdit(goal)}
          >
            Edit
          </Button>
          <Button
            type={ButtonTypes.link}
            size="small"
            tone="danger"
            onClick={() => onDelete(goal)}
          >
            Delete
          </Button>
        </LinkActions>
      </Actions>
    </Card>
  );
};
